import { exportImportManager } from './exportImportManager'
import { APP_STATE_BACKUP_BUSY_MESSAGE } from './appStateBackupProgress'
import {
  beginAppStateBackupProgress,
  completeAppStateBackupProgress,
  failAppStateBackupProgress,
  reportAppStateBackupProgress,
} from './appStateBackupProgressCoordinator'

type BridgeValue = string | number | boolean | null | BridgeValue[] | { [key: string]: BridgeValue }

interface BridgeMessage {
  version: number
  id: string
  kind: string
  payload: Record<string, BridgeValue>
}

interface NativeMessageEvent extends Event {
  detail?: BridgeMessage
}

interface IncomingHydrationTransfer {
  id: string
  documentID: string
  expectedChunkCount: number
  expectedByteCount: number
  chunks: Array<Uint8Array | undefined>
}

const nativeHandler = (window as any).webkit?.messageHandlers?.readerBridge

if (nativeHandler) {
  const adaptBundledRoute = (url?: string | URL | null): string | URL | null | undefined => {
    if (
      window.location.protocol !== 'file:'
      || typeof url !== 'string'
      || !url.startsWith('/')
      || url.startsWith(window.location.pathname)
    ) {
      return url
    }
    return `${window.location.pathname}${window.location.search}#${url}`
  }
  const originalPushState = window.history.pushState.bind(window.history)
  const originalReplaceState = window.history.replaceState.bind(window.history)
  window.history.pushState = (data: any, unused: string, url?: string | URL | null): void => {
    originalPushState(data, unused, adaptBundledRoute(url))
  }
  window.history.replaceState = (data: any, unused: string, url?: string | URL | null): void => {
    originalReplaceState(data, unused, adaptBundledRoute(url))
  }

  let hydrating = false
  let dirtyTimer: number | null = null
  let layoutFrame = 0
  let layoutGeneration = 0
  let observedReader: HTMLElement | null = null
  let readerResizeObserver: ResizeObserver | null = null
  let rememberedReaderScrollTop = 0
  let incomingHydrationTransfer: IncomingHydrationTransfer | null = null
  let snapshotInFlight: Promise<void> | null = null
  let snapshotRerunRequested = false
  const bundleIdentity = process.env.REACT_APP_READER_SOURCE_FINGERPRINT || 'development'
  const transferChunkBytes = 256 * 1024

  const makeID = (): string => (
    typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(16).slice(2)}`
  )

  const post = (kind: string, payload: Record<string, BridgeValue> = {}): void => {
    nativeHandler.postMessage({ version: 1, id: makeID(), kind, payload })
  }

  const postError = (code: string, detail: string): void => {
    post('error', { code, detail })
  }

  const bytesToBase64 = (bytes: Uint8Array): string => {
    let binary = ''
    for (let offset = 0; offset < bytes.length; offset += 0x8000) {
      binary += String.fromCharCode.apply(
        null,
        Array.from(bytes.subarray(offset, Math.min(offset + 0x8000, bytes.length))),
      )
    }
    return btoa(binary)
  }

  const base64ToBytes = (encoded: string): Uint8Array => {
    const binary = atob(encoded)
    const bytes = new Uint8Array(binary.length)
    for (let index = 0; index < binary.length; index += 1) bytes[index] = binary.charCodeAt(index)
    return bytes
  }

  const emptyDocumentState = (): string => JSON.stringify({
    format_version: '3.0.0',
    export_type: 'scholarly_app_state_v3',
    export_timestamp: new Date().toISOString(),
    exported_by: 'The Reader for iPad',
    metadata: {
      application_version: '3.0.0',
      platform: 'iPad',
      compatibility_notes: ['Document-isolated native Reader state.'],
    },
    route_snapshot: { pathname: '/', search: '', hash: '#/workbench' },
    primary_indexed_db: { stores: {} },
    resume_indexed_db: { stores: {} },
    browser_storage: { local_storage: {}, session_storage: {} },
    export_summary: {
      primary_store_counts: {},
      resume_store_counts: {},
      local_storage_keys: 0,
      session_storage_keys: 0,
    },
  })

  const emitDocumentSnapshot = async (): Promise<void> => {
    const operationToken = beginAppStateBackupProgress('export')
    if (!operationToken) {
      postError('snapshotFailed', APP_STATE_BACKUP_BUSY_MESSAGE)
      return
    }
    try {
      const exported = await exportImportManager.exportAppState('json', event => {
        reportAppStateBackupProgress(operationToken, event)
      })
      const readerState = typeof exported === 'string' ? exported : await exported.text()
      const bytes = new TextEncoder().encode(readerState)
      const transferID = makeID()
      const chunkCount = Math.max(1, Math.ceil(bytes.length / transferChunkBytes))
      post('documentSnapshotStart', { transferID, chunkCount, byteCount: bytes.length })
      for (let index = 0; index < chunkCount; index += 1) {
        const start = index * transferChunkBytes
        const chunk = bytes.subarray(start, Math.min(start + transferChunkBytes, bytes.length))
        post('documentSnapshotChunk', { transferID, index, data: bytesToBase64(chunk) })
        if ((index + 1) % 8 === 0 && index + 1 < chunkCount) {
          await new Promise<void>(resolve => window.setTimeout(resolve, 0))
        }
      }
      post('documentSnapshotCommit', { transferID })
      completeAppStateBackupProgress(operationToken, 'export', 'Reader backup prepared')
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Reader snapshot failed.'
      failAppStateBackupProgress(operationToken, 'export', message)
      postError('snapshotFailed', message)
    }
  }

  const snapshotDocument = (): Promise<void> => {
    if (snapshotInFlight) {
      snapshotRerunRequested = true
      return snapshotInFlight
    }
    snapshotInFlight = (async () => {
      do {
        snapshotRerunRequested = false
        await emitDocumentSnapshot()
      } while (snapshotRerunRequested)
    })().finally(() => {
      snapshotInFlight = null
    })
    return snapshotInFlight
  }

  const markDocumentDirty = (): void => {
    if (hydrating) return
    if (dirtyTimer !== null) window.clearTimeout(dirtyTimer)
    dirtyTimer = window.setTimeout(() => {
      dirtyTimer = null
      post('documentDirty', {})
    }, 450)
  }

  const hashText = async (text: string): Promise<string> => {
    if (crypto.subtle) {
      const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(text))
      return Array.from(new Uint8Array(digest)).map(byte => byte.toString(16).padStart(2, '0')).join('')
    }
    let hash = 2166136261
    for (let index = 0; index < text.length; index += 1) {
      hash ^= text.charCodeAt(index)
      hash = Math.imul(hash, 16777619)
    }
    return `fnv1a-${(hash >>> 0).toString(16).padStart(8, '0')}`
  }

  const readerRoot = (): HTMLElement | null => (
    document.querySelector<HTMLElement>('[data-testid="ledger-safe-tradition-reader"]')
  )

  const setFolioAncestors = (root: HTMLElement, enabled: boolean): void => {
    let ancestor: HTMLElement | null = root.parentElement
    while (ancestor && ancestor !== document.body) {
      ancestor.classList.toggle('native-reader-folio-ancestor', enabled)
      ancestor = ancestor.parentElement
    }
  }

  const applyInkMode = (enabled: boolean): void => {
    document.documentElement.dataset.nativeInkMode = enabled ? 'true' : 'false'
    const root = readerRoot()
    if (!root) return
    const innerReader = root.querySelector<HTMLElement>('.cte-reader')
    if (enabled) {
      rememberedReaderScrollTop = innerReader?.scrollTop || 0
      if (innerReader) innerReader.scrollTop = 0
    }
    setFolioAncestors(root, enabled)
    root.classList.toggle('native-reader-folio', enabled)
    if (!enabled && innerReader) {
      requestAnimationFrame(() => { innerReader.scrollTop = rememberedReaderScrollTop })
    }
    requestAnimationFrame(() => requestAnimationFrame(scheduleLayout))
  }

  const publishLayout = async (): Promise<void> => {
    layoutFrame = 0
    const generation = ++layoutGeneration
    const root = readerRoot()
    if (!root) {
      observedReader = null
      post('layoutMapChanged', {
        layoutVersion: 'reader-folio-v1',
        bundleIdentity,
        geometryHash: '',
        sourceWitnessID: '',
        workID: '',
        sourceTextHash: '',
        logicalWidth: document.documentElement.scrollWidth,
        logicalHeight: document.documentElement.scrollHeight,
        fontFamily: '',
        fontSize: 0,
        lineHeight: 0,
        textDirection: 'rtl',
        startUnitID: '',
        endUnitID: '',
        folioActive: false,
        viewportScale: window.visualViewport?.scale || 1,
        units: [],
      })
      return
    }

    if (observedReader !== root) {
      readerResizeObserver?.disconnect()
      observedReader = root
      readerResizeObserver = new ResizeObserver(scheduleLayout)
      readerResizeObserver.observe(root)
      root.querySelector<HTMLElement>('.cte-reader')?.addEventListener('scroll', scheduleLayout, { passive: true })
      applyInkMode(document.documentElement.dataset.nativeInkMode === 'true')
    }

    const seen = new Set<string>()
    const units: Array<{ unitID: string; x: number; y: number; width: number; height: number }> = []
    const sourceParts: string[] = []
    root.querySelectorAll<HTMLElement>('[data-row-id]').forEach(element => {
      const unitID = element.dataset.rowId || ''
      if (!unitID || seen.has(unitID)) return
      seen.add(unitID)
      const rect = element.getBoundingClientRect()
      units.push({
        unitID,
        x: rect.left + window.scrollX,
        y: rect.top + window.scrollY,
        width: rect.width,
        height: rect.height,
      })
      sourceParts.push(`${unitID}\u0000${element.dataset.nativeSourceText || element.textContent || ''}`)
    })
    if (!units.length) return

    const textFlow = root.querySelector<HTMLElement>('.lectio-text-flow')
    const computed = getComputedStyle(textFlow || root)
    const sourceTextHash = await hashText(sourceParts.join('\u0001'))
    if (generation !== layoutGeneration) return

    const logicalWidth = Math.max(document.documentElement.scrollWidth, document.body?.scrollWidth || 0)
    const logicalHeight = Math.max(document.documentElement.scrollHeight, document.body?.scrollHeight || 0)
    const geometryHash = await hashText(JSON.stringify({
      bundleIdentity,
      presentation: [
        root.dataset.nativeReaderSection || '',
        root.dataset.nativeAnnotationGrammar || '',
        root.className,
      ],
      logicalWidth,
      logicalHeight,
      fontFamily: computed.fontFamily,
      fontSize: Number.parseFloat(computed.fontSize) || 0,
      lineHeight: Number.parseFloat(computed.lineHeight) || 0,
      direction: computed.direction || 'rtl',
      units: units.map(unit => ({
        ...unit,
        x: Math.round(unit.x * 1000) / 1000,
        y: Math.round(unit.y * 1000) / 1000,
        width: Math.round(unit.width * 1000) / 1000,
        height: Math.round(unit.height * 1000) / 1000,
      })),
    }))
    if (generation !== layoutGeneration) return

    post('layoutMapChanged', {
      layoutVersion: 'reader-folio-v1',
      bundleIdentity,
      geometryHash,
      sourceWitnessID: root.dataset.nativeSourceWitnessId || '',
      workID: root.dataset.nativeWorkId || '',
      sourceTextHash,
      logicalWidth,
      logicalHeight,
      fontFamily: computed.fontFamily,
      fontSize: Number.parseFloat(computed.fontSize) || 0,
      lineHeight: Number.parseFloat(computed.lineHeight) || 0,
      textDirection: computed.direction || 'rtl',
      startUnitID: units[0].unitID,
      endUnitID: units[units.length - 1].unitID,
      folioActive: document.documentElement.dataset.nativeInkMode === 'true',
      viewportScale: window.visualViewport?.scale || 1,
      units,
    })
  }

  const scheduleLayout = (): void => {
    if (layoutFrame) return
    layoutFrame = requestAnimationFrame(() => { void publishLayout() })
  }

  const hydrateSerializedDocument = async (readerState: string): Promise<void> => {
    hydrating = true
    const operationToken = beginAppStateBackupProgress('import')
    if (!operationToken) {
      postError('hydrateFailed', APP_STATE_BACKUP_BUSY_MESSAGE)
      hydrating = false
      return
    }
    try {
      const serialized = readerState || emptyDocumentState()
      const { result, routeSnapshot } = await exportImportManager.importAppStateBackup(serialized, event => {
        reportAppStateBackupProgress(operationToken, event)
      })
      if (!result.success) {
        throw new Error(result.errors[0]?.message || 'Reader document hydration failed.')
      }
      const restoredRoute = routeSnapshot?.hash || routeSnapshot?.pathname || ''
      const hash = restoredRoute && restoredRoute !== '#/ipad-reader'
        ? restoredRoute.startsWith('#') ? restoredRoute : `#${restoredRoute}`
        : '#/workbench'
      window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}${hash}`)
      window.dispatchEvent(new PopStateEvent('popstate'))
      post('ready', { hydrated: true, route: hash })
      completeAppStateBackupProgress(operationToken, 'import', 'Reader document restored')
      requestAnimationFrame(scheduleLayout)
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Reader hydration failed.'
      failAppStateBackupProgress(operationToken, 'import', message)
      postError('hydrateFailed', message)
    } finally {
      window.setTimeout(() => { hydrating = false }, 500)
    }
  }

  const hydrateDocument = (message: BridgeMessage): void => {
    const serialized = typeof message.payload.readerState === 'string' ? message.payload.readerState : ''
    void hydrateSerializedDocument(serialized)
  }

  const beginHydrationTransfer = (message: BridgeMessage): void => {
    const transferID = typeof message.payload.transferID === 'string' ? message.payload.transferID : ''
    const documentID = typeof message.payload.documentID === 'string' ? message.payload.documentID : ''
    const chunkCount = typeof message.payload.chunkCount === 'number' ? message.payload.chunkCount : 0
    const byteCount = typeof message.payload.byteCount === 'number' ? message.payload.byteCount : -1
    if (!transferID || !Number.isInteger(chunkCount) || chunkCount < 1 || !Number.isInteger(byteCount) || byteCount < 0) {
      postError('hydrateTransferInvalid', 'Reader hydration transfer header is invalid.')
      return
    }
    incomingHydrationTransfer = {
      id: transferID,
      documentID,
      expectedChunkCount: chunkCount,
      expectedByteCount: byteCount,
      chunks: new Array(chunkCount),
    }
  }

  const receiveHydrationChunk = (message: BridgeMessage): void => {
    const transferID = typeof message.payload.transferID === 'string' ? message.payload.transferID : ''
    const index = typeof message.payload.index === 'number' ? message.payload.index : -1
    const encoded = typeof message.payload.data === 'string' ? message.payload.data : ''
    const transfer = incomingHydrationTransfer
    if (!transfer || transfer.id !== transferID || !Number.isInteger(index) || index < 0 ||
        index >= transfer.expectedChunkCount) {
      postError('hydrateTransferInvalid', 'Reader hydration chunk is invalid or out of order.')
      return
    }
    try {
      transfer.chunks[index] = base64ToBytes(encoded)
    } catch (error) {
      postError('hydrateTransferInvalid', error instanceof Error ? error.message : 'Reader hydration chunk is not valid base64.')
    }
  }

  const commitHydrationTransfer = (message: BridgeMessage): void => {
    const transferID = typeof message.payload.transferID === 'string' ? message.payload.transferID : ''
    const transfer = incomingHydrationTransfer
    incomingHydrationTransfer = null
    if (!transfer || transfer.id !== transferID || transfer.chunks.some(chunk => !chunk)) {
      postError('hydrateTransferIncomplete', 'Reader hydration transfer is incomplete.')
      return
    }
    const bytes = new Uint8Array(transfer.expectedByteCount)
    let offset = 0
    for (const chunk of transfer.chunks) {
      if (!chunk || offset + chunk.length > bytes.length) {
        postError('hydrateTransferInvalid', 'Reader hydration transfer exceeds its declared size.')
        return
      }
      bytes.set(chunk, offset)
      offset += chunk.length
    }
    if (offset !== transfer.expectedByteCount) {
      postError('hydrateTransferInvalid', 'Reader hydration transfer size does not match its declaration.')
      return
    }
    void hydrateSerializedDocument(new TextDecoder().decode(bytes))
  }

  window.addEventListener('reader-native-message', event => {
    const message = (event as NativeMessageEvent).detail
    if (!message || message.version !== 1) return
    if (message.kind === 'hydrateDocument') {
      hydrateDocument(message)
    } else if (message.kind === 'hydrateDocumentStart') {
      beginHydrationTransfer(message)
    } else if (message.kind === 'hydrateDocumentChunk') {
      receiveHydrationChunk(message)
    } else if (message.kind === 'hydrateDocumentCommit') {
      commitHydrationTransfer(message)
    } else if (message.kind === 'requestDocumentSnapshot') {
      void snapshotDocument()
    } else if (message.kind === 'setInkMode') {
      applyInkMode(message.payload.enabled === true)
    }
  })

  document.addEventListener('input', markDocumentDirty, true)
  document.addEventListener('change', markDocumentDirty, true)
  document.addEventListener('click', event => {
    const control = (event.target as Element | null)?.closest('button,a,[role="menuitem"]')
    if (control) {
      const href = control instanceof HTMLAnchorElement ? control.getAttribute('href') : null
      if (href?.startsWith('/')) {
        event.preventDefault()
        window.history.pushState({}, '', href)
        window.dispatchEvent(new PopStateEvent('popstate'))
      }
      markDocumentDirty()
      post('selectionChanged', {
        diagnostic: 'interaction',
        value: `${control.tagName.toLowerCase()}:${control.getAttribute('aria-label') || control.textContent?.trim().slice(0, 80) || ''}`,
      })
    }
  }, true)
  window.addEventListener('hashchange', () => {
    post('selectionChanged', { diagnostic: 'hashchange', value: window.location.hash })
  })
  window.addEventListener('error', event => {
    const detail = event.error instanceof Error
      ? event.error.stack || event.error.message
      : `${event.message} (${event.filename}:${event.lineno}:${event.colno})`
    postError('readerRuntimeError', detail)
  })
  window.addEventListener('unhandledrejection', event => {
    const reason = event.reason
    postError(
      'readerUnhandledRejection',
      reason instanceof Error ? reason.stack || reason.message : String(reason),
    )
  })
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden' && !hydrating) void snapshotDocument()
  })
  window.addEventListener('pagehide', () => { if (!hydrating) void snapshotDocument() })
  window.addEventListener('resize', scheduleLayout, { passive: true })
  new MutationObserver(scheduleLayout).observe(document.documentElement, { childList: true, subtree: true })
  document.fonts?.ready?.then(scheduleLayout)
  scheduleLayout()
}

export {}
